import React, { useCallback, useRef, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import Wizard, { Step } from 'components/Wizard';
import { goToCreate } from 'redux/modules/router';
import { selectTemplate } from 'redux/modules/template-wizard/actions';
import {
  templateAspectRatioNameSelector,
  templateIdSelector,
} from 'redux/modules/template-wizard/selectors';
import { Dispatch } from 'redux/types';
import useMergeFlows from '../useMergeFlows';
import {
  AspectRatioConfig,
  useVideoFlow,
  VideoFlowProps,
} from '../VideoWizard';
import { templateWizardError } from './actions';
import { useAudioTemplateFlow } from './flows';
import { AudioFlowStep } from './flows/useAudioTemplateFlow/types';
import { TemplateWizardFlowProps, TemplateWizardFlowType } from './types';
import { block } from './utils';

export type TemplateWizardProps = {
  flowType: TemplateWizardFlowType;
};

const TemplateWizard: React.FC<TemplateWizardProps> = ({ flowType }) => {
  const [stepId, setStepId] = useState<string>();
  const wizardRef = useRef<Wizard>();
  const dispatch = useDispatch<Dispatch>();
  const templateId = useSelector(templateIdSelector);
  const templateAspectRatio = useSelector(templateAspectRatioNameSelector);

  const aspectRatioConfig: AspectRatioConfig = templateAspectRatio
    ? { defaultAspectRatio: templateAspectRatio, isAspectRatioLocked: true }
    : { isAspectRatioLocked: false };

  const handleStepChange = useCallback((toStep: Step) => {
    setStepId(toStep.stepId);
  }, []);

  const handleCancel = useCallback(() => {
    dispatch(goToCreate());
  }, [dispatch]);

  const audioFlow = useAudioTemplateFlow({
    onError: (err, bail) => dispatch(templateWizardError(err, bail)),
    stepId,
    wizardRef,
  } as TemplateWizardFlowProps<AudioFlowStep>);

  const videoFlow = useVideoFlow({
    ...aspectRatioConfig,
    onError: (err, bail) => dispatch(templateWizardError(err, bail)),
    onTemplateSelect: newTemplateId => {
      // Only dispatch when user selects a template; skip when switching to
      // template step (undefined) to avoid Redux-driven re-render that resets view
      if (newTemplateId !== undefined) {
        dispatch(selectTemplate(newTemplateId));
      }
    },
    wizardRef,
    isTemplateLocked: true,
    stepId,
    templateId,
  } as VideoFlowProps);

  const flow = flowType === 'audio' ? audioFlow : videoFlow;

  const baseFlow = {
    bodyClassName: block('body', { [stepId]: true }),
    onStepChange: handleStepChange,
    steps: [],
  };

  const mergedFlow = useMergeFlows(baseFlow, flow);

  return (
    <Wizard
      {...mergedFlow}
      className={block()}
      onCancelClick={handleCancel}
      ref={wizardRef}
    />
  );
};

export default TemplateWizard;
